// Copyright ©️ 2026 eVolve MEP, LLC

import { SelectorPanel } from 'components/Autodesk/extensions/SelectorPanel';
import { useCurrentIsolatedSpools } from 'components/Autodesk/extensions/SpoolsExtension/useCurrentIsolatedSpools';
import { useAutodeskExtensionStoreSnapshot } from 'components/Autodesk/useAutodeskExtensionStore';
import { isNil } from 'helpers/isNotNil';
import { strongKeys } from 'helpers/strongEntries';

/**
 * Renders a searchable, checkable list of spools for isolation in the Forge Viewer.
 */
export const SpoolSelector = () => {
  const { commands, isolatedDbIds } = useAutodeskExtensionStoreSnapshot('apsViewer');

  const spools = useAutodeskExtensionStoreSnapshot('spools');
  const isolatedSpoolNames = useCurrentIsolatedSpools().map(([spoolName]) => spoolName);

  return (
    <SelectorPanel>
      {(search) => {
        if (isNil(spools)) return <div style={{ margin: 8 }}>No spools found.</div>;

        const spoolNames = strongKeys(spools)
          .filter(
            (spoolName) =>
              spoolName.toLowerCase().includes(search.toLowerCase()) || isolatedSpoolNames.includes(spoolName),
          )
          .toSorted();

        return spoolNames.map((spoolName) => (
          <div
            key={spoolName}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              margin: 8,
              marginRight: 10,
            }}
          >
            <input
              id={`spool-${spoolName}-checkbox`}
              type="checkbox"
              style={{ width: 15, height: 15 }}
              checked={isolatedSpoolNames.includes(spoolName)}
              disabled={isNil(commands)}
              onChange={() => {
                if (isNil(commands)) return;
                const dbIds = spools[spoolName]?.objects.map(({ dbId }) => dbId);
                if (isNil(dbIds)) return;
                if (isolatedSpoolNames.includes(spoolName)) {
                  const spoolDbIdSet = new Set(dbIds);
                  const nextIsolatedDbIds = isolatedDbIds.filter((dbId) => !spoolDbIdSet.has(dbId));
                  commands.isolate(nextIsolatedDbIds, true);
                } else {
                  const nextIsolatedDbIds = [...dbIds, ...isolatedDbIds];
                  commands.isolate(nextIsolatedDbIds, true);
                }
              }}
            />
            <label htmlFor={`spool-${spoolName}-checkbox`}>{spoolName}</label>
          </div>
        ));
      }}
    </SelectorPanel>
  );
};
